import React, { useEffect, useRef, useState, useMemo } from "react";
import Classes from "./RecordTable.module.css";
import useScreenWidth from "../../../../../../../../../Hooks/useScreenWidth";
import SkeletonLoading from "../../../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading";
import { Pagination, Stack, LinearProgress } from "@mui/material";
import TableRow from "./TableRow";
import { AddIcon } from "../../../../../../../../../utils/Icons";
import { useSelector } from "react-redux";
import backend from "../../../../../../../../../api";
import { uploadMedia } from "../../../../../../../../../utils/uploadMedia";

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}
function extFromUrl(url = "") {
  const m = url.split("?")[0].match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toUpperCase() : "";
}

export default function RecordTable({ bookingId }) {
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;
  const { token, user } = useSelector((s) => s?.auth || {});
  const userId = user?._id;

  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : undefined), [token]);

  const load = async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const { data } = await backend.get(`/api/bookings/${bookingId}`, { headers });
      const arr = data?.data?.documents || [];
      setDocs(arr);
    } catch (err) {
      console.error("load documents error:", err);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  // Pagination
  const totalPages = Math.ceil(docs.length / itemsPerPage) || 1;
  const start = (currentPage - 1) * itemsPerPage;
  const currentItems = docs.slice(start, start + itemsPerPage);
  const startItem = start + 1;
  const endItem = Math.min(currentPage * itemsPerPage, docs.length);
  const totalItems = docs.length;

  const handleAddClick = () => fileRef.current?.click();

  const handleFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !bookingId) return;

    try {
      // Upload to your upload service (returns URL string)
      const mediaUrl = await uploadMedia(files, setUploading, setProgress);
      if (!mediaUrl) {
        alert("Upload failed: no URL returned.");
        return;
      }

      // Pick first file for meta
      const f = files[0];
      const payload = {
        url: mediaUrl,
        title: f?.name || "",
        kind: f?.type || extFromUrl(mediaUrl),
        size: f?.size || 0,
        byUserId: userId || undefined,
      };

      await backend.post(`/api/bookings/${bookingId}/documents`, payload, { headers });
      await load();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  function handlePageChange(_, pageNum) {
    setCurrentPage(pageNum);
  }

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>

          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}>
              <h5>Name</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.header2}`}>
              <h5>Type</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.header3}`}>
              <h5>Uploaded By</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}>
              <h5>Uploaded</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, alignItems: "center" }}>
                {/* {uploading && (
                  <div style={{ minWidth: 160 }}>
                    <LinearProgress variant="determinate" value={progress} />
                  </div>
                )} */}
                <button className={Classes.addBtn} onClick={handleAddClick} disabled={uploading}>
                  <AddIcon height="12px" fill="white" />
                  <h5>{uploading ? "Uploading..." : "Add Document"}</h5>
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFilesChosen}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading
                length={itemsPerPage}
                height={isMobile ? "3.5rem" : "2.8rem"}
                width="100%"
                br="6px"
                padding="0"
              />
            ) : currentItems && currentItems.length > 0 ? (
              currentItems.map((doc) => (
                <TableRow
                  key={doc._id}
                  value={{
                    _id: doc._id,
                    url: doc.url,
                    Name: doc.title || (doc.url?.split("/").pop() || "Document"),
                    Type: doc.kind || extFromUrl(doc.url) || "File",
                    UploadedBy: doc.uploadedBy?.name
                      || [doc.uploadedBy?.firstName, doc.uploadedBy?.lastName].filter(Boolean).join(" ")
                      || "—",
                    Uploaded: fmtDate(doc.uploadedAt),
                  }}
                  rawDoc={doc}
                  bookingId={bookingId}
                  onChanged={load}
                />
              ))
            ) : (
              <h5 className='placeholderText' style={{ margin: '10px 19px' }} >No Document yet...</h5>
            )}
          </div>
          {currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: "1.2rem" }}>
                {loading
                  ? <SkeletonLoading height="1.2rem" width="10rem" br="4px" padding="0" />
                  : `Showing ${startItem} to ${endItem} of ${totalItems} results`
                }
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading
                  ? <SkeletonLoading height="1.2rem" width="5rem" br="4px" padding="0" />
                  : <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />
                }
              </Stack>
            </div>
          }
        </div>
      </div>
    </div>
  );
}
